import * as stylex from '@stylexjs/stylex'
import { useEffect, useId, useRef } from 'react'

import Icon from './Icon'


const styles = stylex.create({
  s0: {
    position: "fixed",
    inset: "0px",
    zIndex: 100,
    display: "flex",
    alignItems: "end",
    justifyContent: "center",
    backgroundColor: "color-mix(in srgb, #172018 45%, transparent)",
    padding: "0px",
    backdropFilter: "blur(1px)",
    "@media (min-width: 640px)": {
      alignItems: "center",
      padding: "20px",
    },
  },
  s1: {
    display: "flex",
    maxHeight: "93dvh",
    width: "100%",
    maxWidth: "640px",
    flexDirection: "column",
    overflow: "hidden",
    borderTopLeftRadius: "24px",
    borderTopRightRadius: "24px",
    backgroundColor: "#fffefa",
    boxShadow: "0 24px 60px rgba(27,38,29,.28)",
    "@media (min-width: 640px)": {
      borderRadius: "22px",
    },
  },
  s2: {
    display: "flex",
    flexShrink: "0",
    alignItems: "start",
    justifyContent: "space-between",
    gap: "16px",
    borderBottomWidth: 1,
    borderColor: "#e6e9e3",
    backgroundColor: "color-mix(in srgb, #fffefa 95%, transparent)",
    paddingInline: "20px",
    paddingBlock: "20px",
    backdropFilter: "blur(8px)",
    "@media (min-width: 640px)": {
      paddingInline: "28px",
    },
  },
  s3: {
    fontSize: "20px",
    fontWeight: 600,
    letterSpacing: "-.02em",
    color: "#212622",
  },
  s4: {
    marginTop: "4px",
    fontSize: "13px",
    lineHeight: "1.25rem",
    color: "#6a7270",
  },
  s5: {
    display: "flex",
    height: "36px",
    width: "36px",
    flexShrink: "0",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "9999px",
    color: "#66706a",
    transition: "color, background-color, border-color, text-decoration-color, fill, stroke, opacity, box-shadow, transform, filter, backdrop-filter",
    ":hover": {
      backgroundColor: "#eef2ec",
    },
    ":focus-visible": {
      outlineWidth: 2,
      outlineOffset: 2,
      outlineColor: "#3f8654",
    },
    ":disabled": {
      cursor: "not-allowed",
      opacity: 0.4,
    },
  },
  s6: {
    minHeight: "0px",
    overflowY: "auto",
    paddingInline: "20px",
    paddingBottom: "24px",
    paddingTop: "20px",
    "@media (min-width: 640px)": {
      paddingInline: "28px",
      paddingBottom: "28px",
    },
  },
})
type DialogProps = {
	children: React.ReactNode
	closeDisabled?: boolean
	description?: string
  onClose: () => void
  title: string
}

export default function Dialog({ children, closeDisabled = false, description, onClose, title }: DialogProps) {
  const titleID = useId()
  const descriptionID = useId()
  const closeRef = useRef<HTMLButtonElement>(null)
  const dialogRef = useRef<HTMLElement>(null)

  useEffect(() => {
    const initialFocus = dialogRef.current?.querySelector<HTMLElement>('[data-dialog-autofocus]')
    if (initialFocus) {
      initialFocus.focus()
    } else {
      closeRef.current?.focus()
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !closeDisabled) {
        onClose()
      }
      if (event.key !== 'Tab' || !dialogRef.current) {
        return
      }
      const focusable = Array.from(dialogRef.current.querySelectorAll<HTMLElement>('button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled])'))
      if (focusable.length === 0) {
        return
      }
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', onKeyDown)
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.body.style.overflow = previousOverflow
    }
  }, [closeDisabled, onClose])

  const handleClose = closeDisabled ? () => undefined : onClose
  return (
    <div {...stylex.props(styles.s0)} onMouseDown={handleClose}>
      <section aria-describedby={description ? descriptionID : undefined} aria-labelledby={titleID} aria-modal="true" {...stylex.props(styles.s1)} onMouseDown={(event) => event.stopPropagation()} ref={dialogRef} role="dialog">
        <header {...stylex.props(styles.s2)}>
          <div>
            <h2 {...stylex.props(styles.s3)} id={titleID}>{title}</h2>
            {description && <p {...stylex.props(styles.s4)} id={descriptionID}>{description}</p>}
          </div>
          <button aria-label="Close dialog" {...stylex.props(styles.s5)} disabled={closeDisabled} onClick={handleClose} ref={closeRef} type="button"><Icon name="close" /></button>
        </header>
        <div {...stylex.props(styles.s6)}>{children}</div>
      </section>
    </div>
  )
}
